import { useEffect, useMemo, useState } from "react";

import { getAssociationName } from "../utils/subscriptionFormatters";

import { parseSubscriptionDate } from "../utils/subscriptionDates";

const normalizeSearchText = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getDateTimestamp = (value, fallback = Number.MAX_SAFE_INTEGER) => {
  const date = parseSubscriptionDate(value);

  return date ? date.getTime() : fallback;
};

const getAttentionPriority = (subscription) => {
  /*
   * 0 — Paiements en retard :
   * action financière immédiate.
   */
  if (subscription.is_overdue) {
    return 0;
  }

  if (subscription.item_type === "subscription") {
    const days = subscription.days_until_end;

    const hasDays =
      days !== null && days !== undefined && Number.isFinite(Number(days));

    const numericDays = hasDays ? Number(days) : null;

    const renewalStatus = subscription.renewal_status;

    /*
     * 1 — Échéance dans moins
     * de 15 jours.
     */
    if (
      numericDays !== null &&
      numericDays >= 0 &&
      numericDays <= 15 &&
      !["renewed", "refused"].includes(renewalStatus)
    ) {
      return 1;
    }

    /*
     * 2 — Devis envoyé.
     */
    if (renewalStatus === "quote_sent") {
      return 2;
    }

    /*
     * 3 — Échéance entre
     * 16 et 30 jours.
     */
    if (
      numericDays !== null &&
      numericDays > 15 &&
      numericDays <= 30 &&
      !["renewed", "refused"].includes(renewalStatus)
    ) {
      return 3;
    }

    /*
     * 4 — Autres abonnements
     * avec une échéance future.
     */
    if (numericDays !== null && numericDays > 30) {
      return 4;
    }

    /*
     * 7 — Expirés :
     * ils restent accessibles,
     * mais ne polluent plus
     * le haut de la liste.
     */
    if (numericDays !== null && numericDays < 0) {
      return 7;
    }

    return 6;
  }

  /*
   * Prestations ponctuelles.
   */
  return 5;
};

const sortSubscriptions = (subscriptions, sortBy) => {
  const rows = [...subscriptions];

  rows.sort((left, right) => {
    switch (sortBy) {
      case "start_desc":
        return (
          getDateTimestamp(right.start_date, 0) -
          getDateTimestamp(left.start_date, 0)
        );

      case "end_asc":
        return (
          getDateTimestamp(left.end_date) - getDateTimestamp(right.end_date)
        );

      case "amount_desc":
        return Number(right.total_amount || 0) - Number(left.total_amount || 0);

      case "association_asc":
        return getAssociationName(left).localeCompare(
          getAssociationName(right),
          "fr",
          {
            sensitivity: "base",
          },
        );

      case "attention":
      default: {
        const leftPriority = getAttentionPriority(left);

        const rightPriority = getAttentionPriority(right);

        const priorityDifference = leftPriority - rightPriority;

        if (priorityDifference !== 0) {
          return priorityDifference;
        }

        /*
         * Pour les contrats expirés,
         * le plus récent passe avant
         * les très anciens.
         */
        if (leftPriority === 7) {
          return (
            getDateTimestamp(right.end_date, 0) -
            getDateTimestamp(left.end_date, 0)
          );
        }

        /*
         * Prestations ponctuelles :
         * les plus récentes d'abord.
         */
        if (leftPriority === 5) {
          return (
            getDateTimestamp(right.start_date, 0) -
            getDateTimestamp(left.start_date, 0)
          );
        }

        /*
         * Pour les abonnements à venir,
         * l'échéance la plus proche
         * reste prioritaire.
         */
        const dateDifference =
          getDateTimestamp(left.end_date) - getDateTimestamp(right.end_date);

        if (dateDifference !== 0) {
          return dateDifference;
        }

        return Number(right.id || 0) - Number(left.id || 0);
      }
    }
  });

  return rows;
};

export default function useSubscriptionFilters(
  subscriptions = [],
  { initialPageSize = 15 } = {},
) {
  const [search, setSearch] = useState("");
  const [itemType, setItemType] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState("all");

  const [renewalStatus, setRenewalStatus] = useState("all");

  const [lifecycleStatus, setLifecycleStatus] = useState("all");

  const [sortBy, setSortBy] = useState("attention");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const lifecycleStatusOptions = useMemo(() => {
    const statuses = new Set();

    subscriptions.forEach((subscription) => {
      const status = String(subscription.status || "").trim();

      if (status) {
        statuses.add(status);
      }
    });

    return Array.from(statuses).sort((left, right) =>
      left.localeCompare(right, "fr", {
        sensitivity: "base",
      }),
    );
  }, [subscriptions]);

  const filteredSubscriptions = useMemo(() => {
    const normalizedSearch = normalizeSearchText(search);

    const filtered = subscriptions.filter((subscription) => {
      if (itemType !== "all" && subscription.item_type !== itemType) {
        return false;
      }

      if (
        paymentStatus !== "all" &&
        subscription.payment_status !== paymentStatus
      ) {
        return false;
      }

      if (
        renewalStatus !== "all" &&
        subscription.renewal_status !== renewalStatus
      ) {
        return false;
      }

      if (
        lifecycleStatus !== "all" &&
        subscription.status !== lifecycleStatus
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchableContent = normalizeSearchText(
        [
          subscription.id,
          subscription.product,
          subscription.details,
          subscription.status,
          getAssociationName(subscription),
        ]
          .filter(Boolean)
          .join(" "),
      );

      return searchableContent.includes(normalizedSearch);
    });

    return sortSubscriptions(filtered, sortBy);
  }, [
    subscriptions,
    search,
    itemType,
    paymentStatus,
    renewalStatus,
    lifecycleStatus,
    sortBy,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    itemType,
    paymentStatus,
    renewalStatus,
    lifecycleStatus,
    sortBy,
    pageSize,
  ]);

  const totalItems = filteredSubscriptions.length;

  const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);

  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedSubscriptions = useMemo(() => {
    const firstIndex = (currentPage - 1) * pageSize;

    return filteredSubscriptions.slice(firstIndex, firstIndex + pageSize);
  }, [filteredSubscriptions, currentPage, pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (search.trim()) count += 1;
    if (itemType !== "all") count += 1;
    if (paymentStatus !== "all") count += 1;
    if (renewalStatus !== "all") count += 1;
    if (lifecycleStatus !== "all") count += 1;

    return count;
  }, [search, itemType, paymentStatus, renewalStatus, lifecycleStatus]);

  const resetFilters = () => {
    setSearch("");
    setItemType("all");
    setPaymentStatus("all");
    setRenewalStatus("all");
    setLifecycleStatus("all");
    setSortBy("attention");
    setPage(1);
  };

  const setPageSize = (value) => {
    const nextValue = Number(value);

    if (![10, 15, 25, 50].includes(nextValue)) {
      return;
    }

    setPageSizeState(nextValue);
    setPage(1);
  };

  return {
    search,
    setSearch,

    itemType,
    setItemType,

    paymentStatus,
    setPaymentStatus,

    renewalStatus,
    setRenewalStatus,

    lifecycleStatus,
    setLifecycleStatus,

    sortBy,
    setSortBy,

    lifecycleStatusOptions,

    filteredSubscriptions,
    paginatedSubscriptions,

    activeFilterCount,
    resetFilters,

    page: currentPage,
    setPage,

    pageSize,
    setPageSize,

    totalItems,
    totalPages,

    firstVisibleItem: totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1,

    lastVisibleItem: Math.min(currentPage * pageSize, totalItems),
  };
}
